'use client';

import { useFormContext } from 'react-hook-form';
import type {
  ReservationFormInputValues,
  ReservationFormValues,
} from '@eliadmin/core/src/components/reservation/reservation-form/reservation-form-schema';
import type { HousingProperty } from '@eliadmin/core/src/api/housing/housing-types';
import {
  computeReservationPricing,
  countNights,
  DEPOSIT_RATE,
  formatSkDateRange,
  pluralNights,
} from '../date-utils';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import { GDPRTextContactForm } from '@eliadmin/core/src/utils/gdpr-tos/gdpr-text-contact-form';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

interface ReservationFormStep4Props {
  properties: HousingProperty[];
  onGoToStep: (step: number) => void;
}

interface SummaryRowProps {
  label: string;
  value: React.ReactNode;
  onEdit: () => void;
}

function SummaryRow({ label, value, onEdit }: SummaryRowProps) {
  const { t } = useTranslations();
  return (
    <div className="flex w-full items-start justify-between gap-3 rounded-[24px] border p-4 sm:items-center sm:gap-4 md:p-5 lg:p-6">
      <div className="flex w-full flex-col items-start justify-start gap-1">
        <p className="w-full text-sm text-secondary sm:text-base">{label}</p>
        <div className="w-full text-base font-bold text-secondary sm:text-lg">{value}</div>
      </div>
      <button
        type="button"
        onClick={onEdit}
        className="whitespace-nowrap text-sm font-bold tracking-tight text-secondary underline-offset-2 hover:underline sm:text-base"
      >
        {t('text754')}
      </button>
    </div>
  );
}

export function ReservationFormStep4({ properties, onGoToStep }: ReservationFormStep4Props) {
  const { t } = useTranslations();
  const form = useFormContext<ReservationFormInputValues, any, ReservationFormValues>();
  const locale = useLocaleStore(s => s.currentLocale);

  const arrival = form.watch('arrival');
  const departure = form.watch('departure');
  const guestsCount = Number(form.watch('guests_count')) || 2;
  const propertyId = form.watch('property_id');
  const name = form.watch('name');
  const surname = form.watch('surname');
  const email = form.watch('email');

  const arrivalDate = arrival instanceof Date ? arrival : null;
  const departureDate = departure instanceof Date ? departure : null;
  const selectedProperty = properties.find(p => p.id === propertyId) ?? properties[0];

  const nights = arrivalDate && departureDate ? countNights(arrivalDate, departureDate) : 0;
  const pricePerNight = Number(selectedProperty?.price_per_night) || 0;
  const { accommodationTotal, cityTaxTotal, grandTotal, deposit } = computeReservationPricing({
    nights,
    guestsCount,
    pricePerNight,
  });

  const dateRangeLabel =
    arrivalDate && departureDate
      ? `${formatSkDateRange(arrivalDate, departureDate)} · ${pluralNights(nights)}`
      : '–';

  const contactLabel =
    [[name, surname].filter(Boolean).join(' '), email].filter(Boolean).join(' · ') || '–';

  return (
    <div className="flex w-full flex-col items-start justify-start gap-6">
      <div className="flex w-full flex-col items-start justify-start gap-2">
        <SummaryRow label={t('text540')} value={dateRangeLabel} onEdit={() => onGoToStep(1)} />

        <SummaryRow
          label="Ubytovanie"
          value={selectedProperty?.name ?? '–'}
          onEdit={() => onGoToStep(2)}
        />
        <SummaryRow
          label="Hostia"
          value={`${guestsCount} ${guestsCount === 1 ? t('text1840') : t('text1841')}`}
          onEdit={() => onGoToStep(1)}
        />
        <SummaryRow
          label="Kontakt"
          value={<span className="break-all">{contactLabel}</span>}
          onEdit={() => onGoToStep(3)}
        />
      </div>

      <div className="flex w-full flex-col items-start justify-start gap-3 rounded-[24px] bg-[#FFF9EA] p-4 sm:p-5 lg:p-6">
        <div className="flex w-full items-center justify-between gap-3">
          <p className="text-base font-normal text-secondary sm:text-lg">
            {nights} {nights === 1 ? 'noc' : nights <= 4 ? 'noci' : t('text3614')} × {pricePerNight}{' '}
            €/noc · {Math.round(pricePerNight / guestsCount)} €/os
          </p>
          <strong className="whitespace-nowrap text-right text-base font-bold text-secondary sm:text-lg">
            {accommodationTotal} €
          </strong>
        </div>
        <div className="flex w-full items-center justify-between gap-3">
          <p className="text-base font-normal text-secondary sm:text-lg">Mestská daň</p>
          <strong className="whitespace-nowrap text-right text-base font-bold text-secondary sm:text-lg">
            {cityTaxTotal} €
          </strong>
        </div>
        <div className="h-[1px] w-full bg-[#DAD7D1]" />
        <div className="flex w-full items-center justify-between gap-4">
          <strong className="text-left text-lg font-bold tracking-tight text-secondary sm:text-xl lg:text-2xl">
            Spolu (vrátane DPH)
          </strong>
          <strong className="text-right text-lg font-bold tracking-tight text-secondary sm:text-xl lg:text-2xl">
            {grandTotal} €
          </strong>
        </div>
        <p className="text-sm font-normal text-secondary">
          Záloha {Math.round(DEPOSIT_RATE * 100)} % ({deposit} €) splatná do 7 dní od potvrdenia
        </p>
      </div>

      <div className="flex w-full flex-col items-start justify-start gap-3">
        <FormField
          control={form.control}
          name="tos"
          render={({ field }) => (
            <FormItem className="col-span-12">
              <FormControl>
                <div className="flex w-full flex-row items-start gap-3">
                  <Checkbox
                    {...(field as any)}
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    className="mt-0.5"
                  />
                  <FormLabel className="cursor-pointer text-sm font-normal leading-relaxed text-secondary">
                    Súhlasím s pravidlami ubytovania a storno podmienkami. *
                  </FormLabel>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="gdpr"
          render={({ field }) => (
            <FormItem className="col-span-12">
              <FormControl>
                <div className="flex w-full flex-row items-start gap-3">
                  <Checkbox
                    {...(field as any)}
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    className="mt-0.5"
                  />
                  <FormLabel className="cursor-pointer text-sm font-normal leading-relaxed text-secondary">
                    <GDPRTextContactForm locale={locale} />
                  </FormLabel>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}
